/**
 * Weather: a clear / rain / thunder state machine (vanilla's ServerLevel timers),
 * plus the queries the rest of the game asks (isRaining, isRainingAt, ...).
 * Overworld only; the Nether and End have no weather.
 *
 * Durations are vanilla ticks, measured against the day: a day is 24000 ticks
 * and lasts DAY_LENGTH seconds here, so a "tick" of weather is 600/24000 s.
 * The fade in and out of the downpour runs in real time (vanilla's 0.01 per
 * 1/20 s, so five seconds).
 *
 * DOM-free and seeded: the same seed and the same dt sequence give the same
 * weather. `weather` is the shared instance.
 */

import { DAY_LENGTH } from '../constants';
import type { Biome } from '../worldgen/biomes';
import { PRECIP_NONE, PRECIP_RAIN, precipitationAt, type Precip } from '../worldgen/biomeClimate';

/** Weather ticks per real second (24000 ticks per day). */
export const WEATHER_TICKS_PER_SEC = 24000 / DAY_LENGTH;
/** Vanilla ServerLevelData ranges, in ticks. */
export const CLEAR_MIN = 12000, CLEAR_MAX = 180000;
export const RAIN_MIN = 12000, RAIN_MAX = 24000;
export const THUNDER_MIN = 3600, THUNDER_MAX = 15600;
/** Strength change per second while fading (vanilla 0.01 per tick). */
const RAMP_PER_SEC = 0.2;
/** Vanilla thresholds. */
const RAIN_THRESHOLD = 0.2;
const THUNDER_THRESHOLD = 0.9;

export type WeatherKind = 'clear' | 'rain' | 'thunder';

/** What isRainingAt needs from the world; set by the game. */
export interface WeatherWorld {
  /** First free y above the highest solid / liquid block of the column, or -1 when unloaded. */
  topY(x: number, z: number): number;
  biomeAt(x: number, z: number): Biome;
}

/** Saved state (JSON-safe). */
export interface WeatherSave {
  clearTime: number;
  rainTime: number;
  thunderTime: number;
  raining: boolean;
  thundering: boolean;
  rainLevel: number;
  thunderLevel: number;
  rng: number;
}

export class Weather {
  clearTime = 0;
  rainTime = 0;
  thunderTime = 0;
  raining = false;
  thundering = false;
  /** Smoothed 0..1 strengths (vanilla rainLevel / thunderLevel). */
  rainLevel = 0;
  thunderLevel = 0;
  /** 1 on a lightning strike, decays to 0 within ~0.3 s: the sky flash. */
  flash = 0;
  /** Bumped whenever the state is changed from outside (command, bed), for listeners. */
  version = 0;
  world: WeatherWorld | null = null;

  private rng = 1;
  private tickAcc = 0;

  constructor(seed = 1) {
    this.reset(seed);
  }

  /** Back to a clear sky with fresh timers, seeded. */
  reset(seed: number): void {
    this.clearTime = 0; this.rainTime = 0; this.thunderTime = 0;
    this.raining = false; this.thundering = false;
    this.rainLevel = 0; this.thunderLevel = 0; this.flash = 0;
    this.tickAcc = 0;
    this.rng = (seed ^ 0x5bd1e995) | 0;
    this.version++;
  }

  /** Next seeded float in [0, 1) (mulberry32 with the state kept in a field so it can be saved). */
  random(): number {
    this.rng = (this.rng + 0x6d2b79f5) | 0;
    let t = Math.imul(this.rng ^ (this.rng >>> 15), 1 | this.rng);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  private range(lo: number, hi: number): number {
    return lo + Math.floor(this.random() * (hi - lo + 1));
  }

  /** Advance by dt real seconds. */
  update(dt: number): void {
    if (dt <= 0) return;
    this.tickAcc += dt * WEATHER_TICKS_PER_SEC;
    // a long skip (sleeping, a debug jump) is replayed at most this far; the timers re-roll anyway
    if (this.tickAcc > 4000) this.tickAcc = 4000;
    while (this.tickAcc >= 1) {
      this.tickAcc -= 1;
      this.stepTick();
    }
    const r = RAMP_PER_SEC * dt;
    this.rainLevel = approach(this.rainLevel, this.raining ? 1 : 0, r);
    this.thunderLevel = approach(this.thunderLevel, this.thundering ? 1 : 0, r);
    if (this.flash > 0) this.flash = Math.max(0, this.flash - dt * 3);
  }

  /** One vanilla weather tick (ServerLevel.advanceWeatherCycle). */
  private stepTick(): void {
    if (this.clearTime > 0) {
      this.clearTime--;
      this.thunderTime = this.thundering ? 0 : 1;
      this.rainTime = this.raining ? 0 : 1;
      this.thundering = false;
      this.raining = false;
      return;
    }
    if (this.thunderTime > 0) {
      if (--this.thunderTime === 0) this.thundering = !this.thundering;
    } else {
      this.thunderTime = this.thundering ? this.range(THUNDER_MIN, THUNDER_MAX) : this.range(CLEAR_MIN, CLEAR_MAX);
    }
    if (this.rainTime > 0) {
      if (--this.rainTime === 0) this.raining = !this.raining;
    } else {
      this.rainTime = this.raining ? this.range(RAIN_MIN, RAIN_MAX) : this.range(CLEAR_MIN, CLEAR_MAX);
    }
  }

  // --- commands -------------------------------------------------------------

  /** `/weather clear [seconds]`: clear for the given real seconds (default: vanilla's random span). */
  setClear(seconds?: number): void {
    this.clearTime = seconds !== undefined ? Math.max(1, Math.round(seconds * WEATHER_TICKS_PER_SEC)) : this.range(CLEAR_MIN, CLEAR_MAX);
    this.rainTime = 0; this.thunderTime = 0;
    this.raining = false; this.thundering = false;
    this.version++;
  }

  setRain(seconds?: number): void {
    const t = seconds !== undefined ? Math.max(1, Math.round(seconds * WEATHER_TICKS_PER_SEC)) : this.range(RAIN_MIN, RAIN_MAX);
    this.clearTime = 0; this.rainTime = t; this.thunderTime = t;
    this.raining = true; this.thundering = false;
    this.version++;
  }

  setThunder(seconds?: number): void {
    const t = seconds !== undefined ? Math.max(1, Math.round(seconds * WEATHER_TICKS_PER_SEC)) : this.range(THUNDER_MIN, THUNDER_MAX);
    this.clearTime = 0; this.rainTime = t; this.thunderTime = t;
    this.raining = true; this.thundering = true;
    this.version++;
  }

  /** Waking up after sleeping through the night: vanilla wakeUpAllPlayers -> resetWeatherCycle. */
  wake(): void {
    if (!this.raining && !this.thundering) return;
    this.rainTime = 0; this.thunderTime = 0;
    this.raining = false; this.thundering = false;
    this.version++;
  }

  // --- queries ----------------------------------------------------------------

  /** Strength of the rain 0..1 (also while snowing). */
  rainStrength(): number { return this.rainLevel; }
  /** Strength of the thunder 0..1 (vanilla multiplies by the rain level). */
  thunderStrength(): number { return this.thunderLevel * this.rainLevel; }
  isRaining(): boolean { return this.rainLevel > RAIN_THRESHOLD; }
  isThundering(): boolean { return this.thunderStrength() > THUNDER_THRESHOLD; }
  kind(): WeatherKind { return this.isThundering() ? 'thunder' : this.isRaining() ? 'rain' : 'clear'; }

  /** What falls at (x, y, z) right now: PRECIP_NONE / RAIN / SNOW, ignoring roofs. */
  precipAt(x: number, y: number, z: number): Precip {
    if (!this.isRaining() || !this.world) return PRECIP_NONE;
    return precipitationAt(this.world.biomeAt(Math.floor(x), Math.floor(z)), y);
  }

  /** Is the point open to the sky (nothing solid or wet above)? */
  exposed(x: number, y: number, z: number): boolean {
    const top = this.world?.topY(Math.floor(x), Math.floor(z)) ?? -1;
    return top >= 0 && Math.floor(y + 0.01) >= top;
  }

  /** Rain (not snow) is falling on this point: raining, a wet biome, open sky. */
  isRainingAt(x: number, y: number, z: number): boolean {
    return this.precipAt(x, y, z) === PRECIP_RAIN && this.exposed(x, y, z);
  }

  /** Multiplier on sky light: vanilla getSkyDarken, 1 - 5/16 per rain and per thunder strength. */
  skyBrightness(): number {
    return (1 - 0.3125 * this.rainStrength()) * (1 - 0.3125 * this.thunderStrength());
  }

  // --- save -------------------------------------------------------------------

  serialize(): WeatherSave {
    return {
      clearTime: this.clearTime, rainTime: this.rainTime, thunderTime: this.thunderTime,
      raining: this.raining, thundering: this.thundering,
      rainLevel: this.rainLevel, thunderLevel: this.thunderLevel, rng: this.rng,
    };
  }

  /** Restore a saved state (anything malformed is ignored and the seeded defaults stay). */
  load(s: unknown, seed: number): void {
    this.reset(seed);
    if (!s || typeof s !== 'object') return;
    const o = s as Partial<WeatherSave>;
    const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
    this.clearTime = Math.max(0, num(o.clearTime, 0));
    this.rainTime = Math.max(0, num(o.rainTime, 0));
    this.thunderTime = Math.max(0, num(o.thunderTime, 0));
    this.raining = o.raining === true;
    this.thundering = o.thundering === true;
    this.rainLevel = Math.min(1, Math.max(0, num(o.rainLevel, 0)));
    this.thunderLevel = Math.min(1, Math.max(0, num(o.thunderLevel, 0)));
    this.rng = num(o.rng, this.rng) | 0;
  }
}

function approach(v: number, target: number, step: number): number {
  return v < target ? Math.min(target, v + step) : Math.max(target, v - step);
}

/** The shared weather of the running game. */
export const weather = new Weather(1);

/** Handler of `/weather clear|rain|thunder [seconds]`; returns the chat reply. */
export function weatherCommand(args: readonly string[], w: Weather = weather): string {
  const kind = args[0]?.toLowerCase();
  if (kind !== 'clear' && kind !== 'rain' && kind !== 'thunder') {
    return `Weather: ${w.kind()}. Usage: /weather clear|rain|thunder [seconds]`;
  }
  let seconds: number | undefined;
  if (args[1] !== undefined) {
    seconds = Number(args[1]);
    if (!Number.isFinite(seconds) || seconds <= 0 || seconds > 1_000_000) return 'Usage: /weather clear|rain|thunder [seconds]';
  }
  if (kind === 'clear') { w.setClear(seconds); return 'Set the weather to clear'; }
  if (kind === 'rain') { w.setRain(seconds); return 'Set the weather to rain'; }
  w.setThunder(seconds);
  return 'Set the weather to rain & thunder';
}
